import { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FieldError } from "@/components/States";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPES = [{ value: "out_of_time", label: "Out of time" }, { value: "other", label: "Other" }];

export const CancelDialog = ({ open, onOpenChange, onConfirm, busy, tid, address }) => {
  const [type, setType] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      const was = address?.status === "cancelled";
      setType(was ? address.cancel_reason_type || "" : "");
      setReason(was ? address.cancel_reason || "" : "");
      setError("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e) => {
    e.preventDefault();
    if (!type) return setError("Tick Out of time or Other");
    if (type === "other" && !reason.trim()) return setError("Give a reason for cancelling");
    onConfirm({ cancel_reason_type: type, cancel_reason: type === "other" ? reason.trim() : null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`${tid}-cancel-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">Cancel address</DialogTitle>
          <DialogDescription>Why was {address?.full_address || address?.postcode || "this address"}{address?.full_address ? `, ${address.postcode}` : ""} cancelled?</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div className="flex gap-3">
            {TYPES.map((t) => (
              <label key={t.value} className="flex min-h-[44px] flex-1 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-medium">
                <Checkbox checked={type === t.value} onCheckedChange={(v) => { setType(v ? t.value : ""); setError(""); }} data-testid={`${tid}-cancel-${t.value}`} />
                {t.label}
              </label>
            ))}
          </div>
          {type === "other" && (
            <div>
              <Label htmlFor={`${tid}-cancel-reason`}>Reason *</Label>
              <Textarea id={`${tid}-cancel-reason`} rows={4} value={reason} onChange={(e) => { setReason(e.target.value); setError(""); }} maxLength={1000} placeholder="Why was it cancelled?" className="mt-1.5" data-testid={`${tid}-cancel-reason`} />
            </div>
          )}
          <FieldError message={error} testId={`${tid}-cancel-error`} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${tid}-cancel-close`}>Back</Button>
            <Button type="submit" disabled={busy} className="bg-slate-800 hover:bg-slate-900" data-testid={`${tid}-cancel-save`}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save cancellation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const CancelNote = ({ address, testId, onEdit }) => {
  if (address.status !== "cancelled" || !address.cancel_reason_type) return null;
  const text = address.cancel_reason_type === "out_of_time" ? "Out of time" : `Other: ${address.cancel_reason || ""}`;
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border-l-4 border-slate-500 bg-slate-100 p-3 text-sm text-slate-800" data-testid={testId}>
      <p className="flex-1 whitespace-pre-line"><span className="font-semibold">Cancelled: </span>{text}</p>
      {onEdit && <Button size="icon" variant="ghost" onClick={onEdit} aria-label="Edit cancel reason" className="h-7 w-7" data-testid={`${testId}-edit`}><Pencil className="h-3.5 w-3.5" /></Button>}
    </div>
  );
};
